// @flow

import {
  downloadPacketResponseFile,
  downloadResponseFile,
} from 'api/fileServerApi'
import { downloadPdfDocument } from 'api/pdfFillerApi'
import * as React from 'react'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'
import { ZentakeContext } from 'utils/ZentakeContext'

function useResponseDownload(
  response: ?Response,
  packetResponse: ?PacketResponse
): {
  title: string,
  onClick: () => Promise<void>,
  loading: boolean,
} {
  const { teamConfig } = React.useContext(ZentakeContext)

  const [isDownloading, setIsDownloading] = React.useState(false)

  const onDownloadDocument = React.useCallback(async () => {
    await downloadPdfDocument(response?.uid || response?.id || '')
  }, [response?.uid, response?.id])

  const onDownloadPacketResponse = React.useCallback(async () => {
    await downloadPacketResponseFile(packetResponse?.id, {
      includeEmpty: true,
      format: teamConfig?.download_packet === 'pdf' ? 'pdf' : 'zip',
      customerFirstName: packetResponse?.customer?.first_name ?? '',
      customerLastName: packetResponse?.customer?.last_name ?? '',
    })
  }, [
    packetResponse?.customer?.first_name,
    packetResponse?.customer?.last_name,
    packetResponse?.id,
    teamConfig?.download_packet,
  ])

  const onDownloadResponse = React.useCallback(async () => {
    await downloadResponseFile(response?.id, {
      customerFirstName: response?.customer?.first_name ?? '',
      customerLastName: response?.customer?.last_name ?? '',
    })
  }, [
    response?.customer?.first_name,
    response?.customer?.last_name,
    response?.id,
  ])

  const selectResourceToDownload = React.useCallback(async () => {
    if (Boolean(response?.document)) {
      await onDownloadDocument()
      return
    }

    if (Boolean(packetResponse)) {
      await onDownloadPacketResponse()
      return
    }

    await onDownloadResponse()
  }, [
    onDownloadDocument,
    onDownloadPacketResponse,
    onDownloadResponse,
    packetResponse,
    response?.document,
  ])

  const onDownload = React.useCallback(async () => {
    setIsDownloading(true)
    try {
      await selectResourceToDownload()
    } finally {
      setIsDownloading(false)
    }
  }, [selectResourceToDownload])

  return {
    title: 'Download',
    onClick: onDownload,
    loading: isDownloading,
  }
}

export default useResponseDownload
